import type { ApplyRowResult, ScanRow } from "../../../api/contactSlugBackfill.service.js";
import { downloadCsv, escapeCsvValue, sanitizeUserInput } from "../../../utils/csvExport.js";

/**
 * Builds the two CSV files for the contact-slug backfill tool.
 *
 * - `buildDryRunCsv` is the pre-apply file: EVERY scanned row with its planned slug or
 *   skip reason, whether selected or not — the point of the file is to review the whole
 *   candidate set offline before anything is written.
 * - `buildAppliedCsv` is the post-apply record: one row per submitted contact with what
 *   actually happened. The API has no bulk undo, so this file (like the per-row audit
 *   log) is the record of exactly which contacts received which slug — reverting means
 *   setting those contacts' slugs back to NULL.
 */

export const DRY_RUN_HEADERS = ["contactId", "name", "plannedSlug", "skipReason"] as const;

export const APPLIED_HEADERS = ["contactId", "name", "status", "slug"] as const;

/**
 * Formula-injection neutralisation THEN quoting, via the shared csvExport helpers —
 * contact names come from the database, i.e. are untrusted as far as this export is
 * concerned. Quoting alone would not stop Excel evaluating a leading "=".
 */
function csvField(value: string | null): string {
  return escapeCsvValue(sanitizeUserInput(value));
}

/** One row per scanned contact, in the order the scan returned them. */
export function buildDryRunCsv(rows: ScanRow[]): string {
  const lines: string[] = [DRY_RUN_HEADERS.join(",")];
  for (const row of rows) {
    lines.push([row.contactId, row.name, row.plannedSlug, row.skipReason].map(csvField).join(","));
  }
  return `${lines.join("\n")}\n`;
}

/** One row per submitted contact, in the order the apply results answered them. */
export function buildAppliedCsv(results: ApplyRowResult[]): string {
  const lines: string[] = [APPLIED_HEADERS.join(",")];
  for (const result of results) {
    lines.push(
      [result.contactId, result.name, result.status, result.slug ?? null].map(csvField).join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/** Shared `2026-08-18-10-20-30` filename stamp, so both files sort together. */
function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function dryRunFilename(now: Date): string {
  return `contact-slug-backfill-dry-run-${filenameStamp(now)}.csv`;
}

export function appliedFilename(now: Date): string {
  return `contact-slug-backfill-applied-${filenameStamp(now)}.csv`;
}

/**
 * Builds the dry-run CSV and hands it to the browser.
 *
 * @param at Stamps the filename — pass the moment of the download, so two downloads in
 *   one session cannot produce identically named files.
 */
export function downloadDryRunCsv(rows: ScanRow[], at: Date): void {
  downloadCsv(buildDryRunCsv(rows), dryRunFilename(at));
}

/** Builds the post-apply record CSV and hands it to the browser. */
export function downloadAppliedCsv(results: ApplyRowResult[], at: Date): void {
  downloadCsv(buildAppliedCsv(results), appliedFilename(at));
}
